import React, { useCallback, useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material'
import { StartAndMonitorFlow } from './FlowStartandMonitor'
import {
  ContentServiceFlowResults,
  ContentServiceFlowStatus,
  RequestError,
} from './types'
import { startExampleJobFlow, isRequestError } from './utility'

export function FlowStartandMonitorExample() {
  const [flowResults, setFlowResults] =
    useState<ContentServiceFlowResults | null>(null)
  const [flowStatus, setFlowStatus] = useState<ContentServiceFlowStatus>()
  const [failWorker, setFailWorker] = useState(false)
  const [displayDownloadLink, setDisplayDownloadLink] = useState(true)

  const startFlowFunction = async (): Promise<string> => {
    setFlowResults(null) // Clear previous results
    setFlowStatus(undefined)
    const params: Record<string, unknown> = { dependants: 5 }
    if (failWorker) {
      params['fail_worker'] = true
    }
    return startExampleJobFlow(params)
  }

  const handleFlowPoll = useCallback(
    (status: ContentServiceFlowStatus | RequestError | undefined) => {
      if (!status || isRequestError(status)) return
      setFlowStatus(status)
    },
    [],
  )

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Flow Start and Monitor Example
      </Typography>

      <Typography variant="body1" paragraph>
        This example demonstrates how to use the StartAndMonitorFlow component.
        Click the button below to start a flow and monitor its progress.
      </Typography>

      <Box mb={2} display="flex" gap={3}>
        <FormControlLabel
          control={
            <Checkbox
              checked={failWorker}
              onChange={(e) => setFailWorker(e.target.checked)}
            />
          }
          label="Fail Worker"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={displayDownloadLink}
              onChange={(e) => setDisplayDownloadLink(e.target.checked)}
            />
          }
          label="Display Download Link (always displays on failed job)"
        />
      </Box>

      <StartAndMonitorFlow
        disabled={false}
        buttonText="Start Flow"
        processingText="Processing Flow..."
        startFlowFunction={startFlowFunction}
        onFlowComplete={setFlowResults}
        onFlowPoll={handleFlowPoll}
        pollingIntervalMs={2000}
        downloadResults={displayDownloadLink}
      />

      <CustomFlowStatus flowStatus={flowStatus} />

      {flowResults && (
        <Box mt={4}>
          <Typography variant="h5" gutterBottom>
            Results
          </Typography>

          <Box display="flex" flexDirection="column" gap={3}>
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Worker Results
              </Typography>
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(flowResults.data.worker_results, null, 2)}
                </pre>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Dependant Results
              </Typography>
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(flowResults.data.dependant_results, null, 2)}
                </pre>
              </Paper>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Errors
              </Typography>
              <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                <pre
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(flowResults.errors, null, 2)}
                </pre>
              </Paper>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

function CustomFlowStatus({
  flowStatus,
}: {
  flowStatus?: ContentServiceFlowStatus
}) {
  if (!flowStatus) return null

  const { worker_jobs, dependant_jobs, completed } = flowStatus.data

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Typography variant="h6">Custom Status</Typography>
        <Chip
          label={completed ? 'Completed' : 'Processing'}
          color={completed ? 'success' : 'warning'}
          size="small"
        />
      </Box>

      <Box display="flex" gap={6} justifyContent="center">
        <JobPieChart title="Worker Jobs" jobs={worker_jobs} />
        <JobPieChart title="Dependant Jobs" jobs={dependant_jobs} />
      </Box>
    </Paper>
  )
}

const COLORS = {
  completed: '#4caf50',
  failed: '#f44336',
  pending: '#ffc947',
}

function JobPieChart({
  title,
  jobs,
}: {
  title: string
  jobs: { completed: number; failed: number; pending: number; total: number }
}) {
  const segments = [
    { value: jobs.completed, color: COLORS.completed, label: 'Completed' },
    { value: jobs.failed, color: COLORS.failed, label: 'Failed' },
    { value: jobs.pending, color: COLORS.pending, label: 'Pending' },
  ]

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
      <Typography variant="subtitle2">{title}</Typography>
      <PieChart segments={segments} size={120} />
      <Box display="flex" gap={2} mt={1}>
        {segments.map((s) => (
          <Box key={s.label} display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: s.color,
              }}
            />
            <Typography variant="caption">
              {s.label}: {s.value}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Total: {jobs.total}
      </Typography>
    </Box>
  )
}

function PieChart({
  segments,
  size = 120,
}: {
  segments: { value: number; color: string; label: string }[]
  size?: number
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  const radius = size / 2

  if (total === 0) {
    return (
      <svg width={size} height={size}>
        <circle
          cx={radius}
          cy={radius}
          r={radius}
          fill="#e0e0e0"
          stroke="#bdbdbd"
          strokeWidth={1}
        />
        <text
          x={radius}
          y={radius}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={12}
          fill="#757575"
        >
          No jobs
        </text>
      </svg>
    )
  }

  let currentAngle = -Math.PI / 2

  const paths = segments
    .filter((s) => s.value > 0)
    .map((segment, i) => {
      const angle = (segment.value / total) * 2 * Math.PI
      const startX = radius + radius * Math.cos(currentAngle)
      const startY = radius + radius * Math.sin(currentAngle)
      currentAngle += angle
      const endX = radius + radius * Math.cos(currentAngle)
      const endY = radius + radius * Math.sin(currentAngle)
      const largeArc = angle > Math.PI ? 1 : 0

      if (segment.value === total) {
        return (
          <circle
            key={i}
            cx={radius}
            cy={radius}
            r={radius}
            fill={segment.color}
          />
        )
      }

      return (
        <path
          key={i}
          d={`M ${radius} ${radius} L ${startX} ${startY} A ${radius} ${radius} 0 ${largeArc} 1 ${endX} ${endY} Z`}
          fill={segment.color}
        />
      )
    })

  return (
    <svg width={size} height={size}>
      {paths}
    </svg>
  )
}
